import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import dayjs from 'dayjs';

import BbForm from 'common/components/BbForm';
import BbField from 'common/components/BbField';
import DatePicker from 'common/components/DatePicker';
import BbHorizontalSlider from 'common/components/BbHorizontalSlider';
import SpinnerLoading from 'common/components/SpinnerLoading';

import HintMessage from 'components/HintMessage';

import useCatalogFilters from 'composables/useCatalogFilters';

import AutomaticReservationAppointments from 'assets/graphql/queries/catalog/AutomaticReservationAppointments.graphql';
import PartnersDaysReservationAvailability from 'assets/graphql/queries/catalog/PartnersDaysReservationAvailability.graphql';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat); //TODO pasar a un helper

import {
  getTextFromDate,
  getISOStringWithoutTime,
  formatTime,
} from './dateFormatter';

@Component({
  apollo: {
    days_reservation_availability: {
      query: PartnersDaysReservationAvailability,
      variables() {
        return {
          reservation_config_id: this.reservationConfig.id,
          code_id: this.codeId,
          all_availability: this.isComboActivity || this.isReservationBuy,
        };
      },
      skip() {
        this.$emit('isLoading', !this.location);
        return !this.reservationConfig;
      },
    },
    appointments: {
      query: AutomaticReservationAppointments,
      variables() {
        return {
          gral_reservation_config_id: this.reservationConfig.id,
          date: this.date.date,
          min_quotas: this.activityQuantity || 1,
        };
      },
      // wait until date is selected
      skip() {
        return !this.date;
      },
    },
  },
  props: {
    activityId: {
      type: [String, Number],
      required: true,
    },
    reservationConfig: {
      type: Object,
      required: true,
    },
    date: {
      type: Object,
    },
    appointment: {
      type: Object,
    },
    expirationDate: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      required: true,
    },
    code: {
      type: [String, Object],
      required: false,
      default: null,
    },
    codeId: {
      type: Number,
      required: false,
      default: null,
    },
    location: {
      type: Object,
      required: true,
    },
    codeExpirationDate: {
      type: String,
      required: false,
      default: '',
    },
    isReservationBuy: {
      type: Boolean,
      required: false,
      default: false,
    },
    isComboActivity: {
      type: Boolean,
      required: false,
      default: false,
    },
    activityQuantity: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  components: {
    BbForm,
    BbField,
    BbHorizontalSlider,
    DatePicker,
    HintMessage,
    SpinnerLoading,
  },
  setup() {
    const { getFormattedDate, getSelectedSchedule } = useCatalogFilters();
    return { getFormattedDate, getSelectedSchedule };
  },
})
export default class CatalogSelectReservationDateStepComponent extends Vue {
  showDatePicker = false;

  formatAppointmentTime(appointment) {
    return appointment.end_time
      ? `${formatTime(appointment.time)} a ${formatTime(appointment.end_time)}`
      : formatTime(appointment.time);
  }

  /**
   * @param {Date} date comes from DatePicker component when user clicks in one date
   */

  setFormDate(date) {
    const newDate = this.days_reservation_availability.find(
      day => day.date === getISOStringWithoutTime(date),
    );
    this.$emit('update:date', newDate);
    this.showDatePicker = false;
  }

  getTextFromDate(date) {
    return getTextFromDate(date);
  }

  toggleDatePicker() {
    this.showDatePicker = !this.showDatePicker;
  }

  get sortedAppointments() {
    return this.appointments?.sort((a, b) => {
      return a.time.localeCompare(b.time);
    });
  }

  /**
   * Filter appointments by it's hour to avoid showing previous hours
   */
  get availableAppointments() {
    if (!this.sortedAppointments || this.thereIsNoReservationConfigYet)
      return [];
    const selectedDate = dayjs(this.date.date);
    if (selectedDate.isSame(dayjs(), 'date')) {
      return this.sortedAppointments.filter(appointment => {
        return parseInt(appointment.time.slice(0, 2)) > dayjs().hour();
      });
    }
    if (this.getSelectedSchedule) {
      let selectedAppointment = this.sortedAppointments.find(appointment => {
        return appointment.time === this.getSelectedSchedule;
      });
      this.$emit('update:appointment', selectedAppointment);
    }
    return this.sortedAppointments;
  }

  /**
   * parse date from 'YYYY-MM-DD' to 'DD/MM/YYYY' so DatePicker gets its 'value'
   * prop as it expects.
   */
  get dateFormattedForDatePicker() {
    if (this.date) {
      return this.date.date.split('-').reverse().join('/');
    }
    return null;
  }

  get disabledDates() {
    if (!this.days_reservation_availability.length) return {};
    return {
      to: dayjs(this.days_reservation_availability[0].date).toDate(),
      from: this.lastDayToDisplay,
      dates: this.unavailableDates,
    };
  }

  get lastDayToDisplay() {
    if (!this.expirationDate) {
      return dayjs(this.lastDateToReserve).toDate();
    }
    return dayjs(this.expirationDate).isBefore(this.lastDateToReserve)
      ? dayjs(this.expirationDate).toDate()
      : dayjs(this.lastDateToReserve).toDate();
  }

  get thereIsNoReservationConfigYet() {
    return !this.reservationConfig;
  }

  get lastDateToReserve() {
    return dayjs(
      this.days_reservation_availability[
        this.days_reservation_availability.length - 1
      ].date,
    );
  }

  get availableDates() {
    if (!this.days_reservation_availability) return [];
    const numberOfDates = 3;
    let firstDates = [];

    for (let i = 0; i < this.days_reservation_availability.length; i++) {
      if (firstDates.length === numberOfDates) {
        break;
      }
      if (this.days_reservation_availability[i].is_available) {
        firstDates.push(this.days_reservation_availability[i]);
      }
    }
    if (firstDates.length) {
      let selectedDay = this.days_reservation_availability.find(
        date => date.date === this.getFormattedDate,
      );
      this.$emit('update:date', selectedDay ?? firstDates[0]);
    }
    return firstDates;
  }

  get unavailableDates() {
    return this.days_reservation_availability
      .filter(date => !date.is_available)
      .map(date => dayjs(date.date).toDate());
  }

  get isUsingCoverManager() {
    return this.reservationConfig?.cover_manager_reservation;
  }

  get hasActiveRange() {
    return this.availableAppointments.some(element => {
      return element.end_time != null && this.appointment?.end_time;
    });
  }

  get noReservationAvailabilityText() {
    return this.isReservationBuy
      ? 'No hay fechas disponibles para esta experiencia'
      : 'No hay fechas disponibles para esta experiencia durante la vigencia de tu código';
  }

  scrollToAppointment(appointmentData) {
    const appointmentIndex = this.availableAppointments.findIndex(
      appointment => appointment.id === appointmentData.id,
    );
    const appointmentLabel = document.querySelector(
      `label[for="appointment-${appointmentIndex}"]`,
    );
    appointmentLabel.scrollIntoView({ behavior: 'smooth' });
  }

  @Watch('appointment')
  onAppointmentChange(val) {
    if (val) this.scrollToAppointment(val);
  }

  @Watch('availableAppointments')
  onAvailableAppointmentsChange(appointments) {
    // If there is only one available appointment, select it
    if (
      appointments &&
      appointments.length === 1 &&
      appointments[0].is_available &&
      !this.appointment
    ) {
      this.$emit('update:appointment', appointments[0]);
    }
  }
}
